// apps/frontend/src/components/feedback/AppFeedbackPrompt.tsx
// Modal de collecte d'avis sur l'app Glowp. Declenchee soit via :
//  - dispatchEvent('glowp:openFeedback', { detail: { context } }) (trigger
//    contextuel : 1ere publication, perso boutique, 5e visite)
//  - bouton "Partager mon experience" dans /account (context SPONTANEOUS)
//
// Identite Glowp : pas un dialogue NPS robotique. Wording chaleureux,
// design epure, fermable a tout moment. 1 question principale (note),
// le reste optionnel. Pas de friction.

import React, { useEffect, useState } from 'react';
import { Star, X, Sparkles, Loader2, Check } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../hooks/useAuth';

export type FeedbackContext =
  | 'FIRST_PUBLISH'
  | 'SHOP_CUSTOMIZED'
  | 'FREQUENT_VISIT'
  | 'SPONTANEOUS';

const CONTEXT_HEADLINE: Record<FeedbackContext, string> = {
  FIRST_PUBLISH: 'Bravo pour ta première annonce ✨',
  SHOP_CUSTOMIZED: 'Ta boutique a du caractère ✨',
  FREQUENT_VISIT: 'Tu reviens souvent, on adore',
  SPONTANEOUS: 'Comment trouves-tu Glowp ?',
};

const CONTEXT_QUESTION: Record<FeedbackContext, string> = {
  FIRST_PUBLISH: "Comment as-tu trouvé l'expérience de publication ?",
  SHOP_CUSTOMIZED: "Qu'est-ce qui t'a marqué dans la personnalisation ?",
  FREQUENT_VISIT: "Qu'est-ce qui te plaît dans Glowp ?",
  SPONTANEOUS: 'Ton avis nous aide à construire la meilleure app possible.',
};

const TAG_OPTIONS = [
  { key: 'Design', icon: '🎨' },
  { key: 'Simple', icon: '📱' },
  { key: 'Communauté', icon: '💬' },
  { key: 'Boutique', icon: '🛍️' },
  { key: 'Rapide', icon: '⚡' },
  { key: 'Inspirant', icon: '✨' },
  { key: 'Fonctionnalités', icon: '🔧' },
  { key: 'Personnalisation', icon: '🎭' },
] as const;

// Local key pour ne pas re-prompter (memorise les contexts deja soumis).
// La BDD a aussi un unique constraint (user_id, context) mais le local
// evite l'aller-retour API pour decider de prompter.
const LS_PROMPTED_PREFIX = 'glowp_feedback_prompted_';

export function markPrompted(context: FeedbackContext): void {
  try {
    localStorage.setItem(LS_PROMPTED_PREFIX + context, String(Date.now()));
  } catch {
    /* silent */
  }
}

export function hasBeenPrompted(context: FeedbackContext): boolean {
  try {
    return Boolean(localStorage.getItem(LS_PROMPTED_PREFIX + context));
  } catch {
    return false;
  }
}

// Trigger global : appelable depuis n'importe ou via dispatchEvent.
export function triggerAppFeedback(context: FeedbackContext): void {
  window.dispatchEvent(
    new CustomEvent('glowp:openFeedback', { detail: { context } })
  );
}

const AppFeedbackPrompt: React.FC = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<FeedbackContext>('SPONTANEOUS');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [consentPublish, setConsentPublish] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const ctx: FeedbackContext = detail.context || 'SPONTANEOUS';
      // Reset form
      setContext(ctx);
      setRating(0);
      setHoverRating(0);
      setTags([]);
      setComment('');
      setConsentPublish(true);
      setSubmitting(false);
      setSubmitted(false);
      setError(null);
      setOpen(true);
    };
    window.addEventListener('glowp:openFeedback', handler);
    return () => window.removeEventListener('glowp:openFeedback', handler);
  }, []);

  if (!open || !user) return null;

  const toggleTag = (tag: string) => {
    setTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      setError("Choisis une note avant d'envoyer");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('glowp_access_token');
      const res = await fetch(`${API_BASE_URL}/api/v1/app-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          rating,
          tags,
          comment: comment.trim(),
          context,
          consentPublish,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Erreur ${res.status}`);
      }
      markPrompted(context);
      setSubmitted(true);
      // Track soumission reussie en BDD pour audit admin
      try {
        fetch(`${API_BASE_URL}/api/v1/analytics/event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: 'app_feedback_submitted',
            page: window.location.pathname,
            properties: { rating, context, has_comment: !!comment.trim() },
          }),
        }).catch(() => {
          /* silent */
        });
      } catch {
        /* silent */
      }
      // Auto-close apres 2.5s
      setTimeout(() => setOpen(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    // On marque quand meme comme prompted pour ne pas re-spammer si l'user
    // a explicitement ferme. Il pourra toujours laisser un avis depuis
    // le bouton profil (context SPONTANEOUS).
    markPrompted(context);
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4 animate-in fade-in duration-200"
      onClick={handleSkip}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-md p-5 sm:p-6 animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-300"
        onClick={e => e.stopPropagation()}
      >
        {submitted ? (
          <div className="text-center py-6">
            <div
              className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3"
              style={{ backgroundColor: 'var(--brand-light)' }}
            >
              <Check className="w-7 h-7 text-brand-500" />
            </div>
            <p className="text-base font-semibold text-brand-dark">
              Merci, c'est noté
            </p>
            <p className="text-sm text-brand-warm mt-1">
              Ça nous aide vraiment.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brand-500" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-brand-500">
                  Glowp
                </h2>
              </div>
              <button
                onClick={handleSkip}
                className="p-1 -mt-1 -mr-1 rounded-lg hover:bg-brand-sand text-brand-muted hover:text-brand-warm"
                aria-label="Fermer"
                disabled={submitting}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <h3 className="text-lg sm:text-xl font-bold text-brand-dark leading-tight">
              {CONTEXT_HEADLINE[context]}
            </h3>
            <p className="text-sm text-brand-warm mt-1.5 leading-relaxed">
              {CONTEXT_QUESTION[context]}
            </p>

            {/* Etoiles */}
            <div className="flex items-center justify-center gap-1 mt-5 mb-3">
              {[1, 2, 3, 4, 5].map(n => {
                const filled = n <= (hoverRating || rating);
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1.5 rounded-lg hover:scale-110 active:scale-95 transition-transform"
                    aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
                  >
                    <Star
                      className={`w-8 h-8 ${
                        filled
                          ? 'text-brand-500 fill-brand-500'
                          : 'text-brand-border'
                      }`}
                      strokeWidth={1.5}
                    />
                  </button>
                );
              })}
            </div>

            {/* Tags (apparaissent une fois la note posee) */}
            {rating > 0 && (
              <div className="mt-2">
                <p className="text-xs text-brand-warm mb-2">
                  C'est top parce que… (optionnel)
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {TAG_OPTIONS.map(opt => {
                    const active = tags.includes(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => toggleTag(opt.key)}
                        className="px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all"
                        style={{
                          backgroundColor: active
                            ? 'var(--brand-500)'
                            : 'white',
                          color: active ? 'white' : 'var(--brand-warm)',
                          borderColor: active
                            ? 'var(--brand-500)'
                            : 'var(--brand-border)',
                        }}
                      >
                        <span className="mr-1">{opt.icon}</span>
                        {opt.key}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Comment libre — encourage a expliquer (l'avis sans texte
                a peu de valeur pour ameliorer le produit). On garde
                'optionnel' mais on prompt activement. */}
            {rating > 0 && (
              <div className="mt-4">
                <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                  Ce qui t'a marquee, en bien ou en moins bien ?
                </label>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value.slice(0, 500))}
                  rows={4}
                  placeholder={
                    rating >= 4
                      ? "Ex : Ce qui m'a plu, ce que je referais, ce que mes amies aiment..."
                      : "Ex : Ce qui m'a deçue, ce qui pourrait etre mieux, ce que j'attendais..."
                  }
                  className="w-full p-3 rounded-xl border border-brand-border bg-brand-offwhite text-sm placeholder:text-brand-muted focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 resize-none leading-relaxed"
                />
                <div className="flex items-center justify-between mt-1">
                  {comment.length === 0 ? (
                    <p className="text-[11px] text-brand-warm italic">
                      Quelques mots concrets nous aident vraiment plus que les
                      etoiles seules.
                    </p>
                  ) : comment.length < 30 ? (
                    <p className="text-[11px] text-amber-700">
                      Quelques phrases de plus rendraient ton avis encore plus
                      utile.
                    </p>
                  ) : (
                    <p className="text-[11px] text-emerald-700">
                      Merci, c'est exactement le genre de retour qui aide.
                    </p>
                  )}
                  {comment.length > 0 && (
                    <p className="text-[10px] text-brand-muted">
                      {comment.length} / 500
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Consent publication */}
            {rating > 0 && (
              <label className="flex items-start gap-2 mt-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consentPublish}
                  onChange={e => setConsentPublish(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-brand-500 accent-brand-500 cursor-pointer shrink-0"
                />
                <span className="text-xs text-brand-warm leading-snug">
                  J'accepte que mon avis soit partagé (prénom et photo si
                  renseignés). Tu peux changer d'avis à tout moment depuis ton
                  profil.
                </span>
              </label>
            )}

            {error && (
              <p className="text-xs text-red-600 mt-2 font-medium">{error}</p>
            )}

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={handleSkip}
                disabled={submitting}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-brand-warm hover:bg-brand-sand transition-colors disabled:opacity-50"
              >
                Plus tard
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || rating < 1}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--brand-500)' }}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Envoyer'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AppFeedbackPrompt;
